import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { BaseModel } from '@core/models/base-model';
import { ValueObject } from '@core/models/value-object.model';

export type FormStatus = 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED';

export type FormModel<M extends BaseModel> = {
  [key in keyof M]: M[key] extends ValueObject[]
    ? string[] // ValueObject[] in model means string[] in form model
    : M[key] extends ValueObject
    ? string // ValueObject in model means string in form model
    : M[key]; // else use model type
};

type NonFunctionPropertyNames<T> = { [K in keyof T]: T[K] extends Function ? never : K }[keyof T];

export type TypedFormControls<M extends BaseModel> = Record<NonFunctionPropertyNames<M>, AbstractControl>;

export type TypedFormGroup<M extends BaseModel> = FormGroup & {
  controls: TypedFormControls<M>;
  value: FormModel<M>;
};

export function mapIfDefined<T, M>(testValue: T, mapFn: () => M): M | null {
  if (testValue === undefined || testValue === null) {
    return null;
  }

  return mapFn();
}

export function normalizeFormModelValues<M extends BaseModel>(formModel: FormModel<M>): FormModel<M> {
  const fm = formModel;

  for (const key in fm) {
    if (Object.prototype.hasOwnProperty.call(fm, key)) {
      if (fm[key] === '') {
        fm[key] = null;
      }
    }
  }

  return fm;
}

/**
 * Retrive form model for dirty form or null
 */
export function cacheForm<TModel extends BaseModel>(form: TypedFormGroup<TModel>): FormModel<TModel> | null {
  let formModel: FormModel<TModel> = null;

  if (form.dirty) {
    formModel = form.value;
  }

  return formModel;
}

/**
 * Fill form with values and mark it dirty
 */
export function recreateCachedForm<TModel extends BaseModel>(
  form: TypedFormGroup<TModel>,
  formModel: FormModel<TModel>,
): void {
  form.patchValue(formModel);
  form.markAsDirty();
}

/**
 * Typecast in templates
 * @see https://github.com/angular/angular/issues/34522
 * @see https://github.com/angular/angular/issues/28731
 */
export const asFormArray = (x: unknown): FormArray => x as FormArray;

/**
 * Cast AbstractControl to FormControl for templates
 * Use when FormGroup.controls[key] returns AbstractControl but FormControl is required
 */
export const asFormControl = (control: AbstractControl | null | undefined): FormControl => control as FormControl;

/**
 * Cast unknown to TypedFormGroup for templates with async pipe
 * Use when Observable<FormGroup> | async infers as unknown in strict mode
 */
export const asTypedFormGroup = <M extends BaseModel>(x: unknown): TypedFormGroup<M> => x as TypedFormGroup<M>;
